import { useEffect, useEffectEvent } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import { useSyncAndDownloadMutation } from '#accounts';
import { useAccounts } from '#hooks/useAccounts';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';
import { registrarUso } from '#uso';

import { tocaSincronizarSolo, yaSincronizadoHoy } from './banco';

/** Día (aaaa-mm-dd) del último intento automático en este dispositivo. */
const CLAVE_ULTIMO_DIA = 'rumbo:sync-auto-dia';

function leerUltimoDia(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_ULTIMO_DIA);
  } catch {
    return null;
  }
}

function guardarUltimoDia(dia: string) {
  try {
    window.localStorage.setItem(CLAVE_ULTIMO_DIA, dia);
  } catch {
    // Sin almacenamiento: como mucho se repetirá en la próxima apertura.
  }
}

/**
 * Sincroniza el banco solo, como mucho una vez al día por dispositivo: al
 * abrir la app (o volver a ella) si hoy aún no se ha intentado y alguna
 * cuenta enlazada lleva horas sin sincronizar. Lo nuevo entra «sin
 * revisar», como al sincronizar a mano. Para más, deslizar en Cuentas.
 */
export function useSincronizacionAutomatica() {
  const { data: cuentas, isSuccess } = useAccounts();
  const estadoServidor = useSyncServerStatus();
  const syncAndDownload = useSyncAndDownloadMutation();

  const quizasSincronizar = useEffectEvent(() => {
    if (!isSuccess || !cuentas || estadoServidor !== 'online') {
      return;
    }
    if (document.visibilityState !== 'visible' || syncAndDownload.isPending) {
      return;
    }
    const hoy = monthUtils.currentDay();
    if (yaSincronizadoHoy(leerUltimoDia(), hoy)) {
      return;
    }
    if (!tocaSincronizarSolo(cuentas)) {
      return;
    }
    guardarUltimoDia(hoy);
    registrarUso('banco_sincronizar', { origen: 'auto', tipo: 'todas' });
    syncAndDownload.mutate({});
  });

  useEffect(() => {
    // Un poco después de abrir, para no pisar la sincronización inicial.
    const espera = window.setTimeout(() => quizasSincronizar(), 3000);
    const alVolver = () => quizasSincronizar();
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearTimeout(espera);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [isSuccess, estadoServidor]);
}
